import { findMapElementById } from "./findMapElementById";

// Node-to-edge snapping: find an edge that the given point lies on (within
// tolerancePx in screen space), so a newly placed node can split it.
// Returns { edge, snappedPosition } for the closest match, or null.
export function findEdgeToSplit(point, mapElements, map, tolerancePx = 12) {
  if (!map || !Array.isArray(mapElements)) return null;
  const click = map.latLngToContainerPoint({ lat: point[0], lng: point[1] });
  let best = null;

  mapElements.forEach((edge) => {
    if (edge.type !== "edge") return;
    const startNode = findMapElementById(mapElements, edge.startNodeId);
    const endNode = findMapElementById(mapElements, edge.endNodeId);
    if (!startNode?.position || !endNode?.position) return;

    const a = map.latLngToContainerPoint({
      lat: startNode.position[0],
      lng: startNode.position[1],
    });
    const b = map.latLngToContainerPoint({
      lat: endNode.position[0],
      lng: endNode.position[1],
    });

    const abx = b.x - a.x;
    const aby = b.y - a.y;
    const lenSq = abx * abx + aby * aby;
    if (lenSq === 0) return;

    // projection factor of the click onto the segment
    const t = ((click.x - a.x) * abx + (click.y - a.y) * aby) / lenSq;
    // ignore hits at the very endpoints (would create degenerate edges)
    if (t < 0.05 || t > 0.95) return;

    const projX = a.x + t * abx;
    const projY = a.y + t * aby;
    const dist = Math.hypot(click.x - projX, click.y - projY);
    if (dist <= tolerancePx && (!best || dist < best.dist)) {
      const snapped = map.containerPointToLatLng({ x: projX, y: projY });
      best = { edge, dist, snappedPosition: [snapped.lat, snapped.lng] };
    }
  });

  return best;
}

// Fresh pipe properties for the split halves (recomputed by the backend later).
function freshEdgeCalc() {
  return [
    { key: "diameter", label: "diameter", type: "number", unit: "m", value: null },
    { key: "outlet_offset", label: "outlet_offset", type: "number", unit: "m", value: null },
    { key: "first_year_pipe_flow", label: "first_year_pipe_flow", type: "number", unit: "l/s", value: null },
    { key: "final_year_pipe_flow", label: "final_year_pipe_flow", type: "number", unit: "l/s", value: null },
  ];
}

// Replaces `edge` with two edges joined at `node`, keeping the edge's props.
// `makeId` creates a unique id; `startName` is the name for the first half
// (the second half gets startName + 1).
export function splitEdgeWithNode(mapElements, edge, node, makeId, startName) {
  const edgeA = {
    ...edge,
    id: makeId(),
    name: startName,
    startNodeId: edge.startNodeId,
    endNodeId: node.id,
    isSaved: false,
    calc: freshEdgeCalc(),
  };
  const edgeB = {
    ...edge,
    id: makeId(),
    name: startName + 1,
    startNodeId: node.id,
    endNodeId: edge.endNodeId,
    isSaved: false,
    calc: freshEdgeCalc(),
  };
  return mapElements
    .filter((element) => element.id !== edge.id)
    .concat([node, edgeA, edgeB]);
}
